import { useContext, useEffect, useState } from "react";
import { SUBMISSION_WL } from "../../../config/constants";
import AppUserContext from "../../../context/app-user-context";
import AppUser from "../../../modules/app-user/app-user";
import ApiSubmissionDate from "../../../modules/api/submission-date";
import { Link, useNavigate } from "react-router-dom";
import { HiPencil, HiPlus } from "react-icons/hi";
import ReportItemListing from "../../../components/portal/ReportItemListing";
import { ROUTES } from "../../../routes";
import FallbackEmptyValue from "../../../components/static/FallbackEmptyValue";
import CohortRangeLabel from "../../../components/static/CohortRangeLabel";
import ApiSubmission from "../../../modules/api/submission";
import ModuleDate from "../../../modules/date";

export default function CompWLIndex() {
  const user = useContext(AppUserContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    AppUser.refreshUser(["avatar"]);

    ApiSubmission.overview(user?.activeGroup?._id, SUBMISSION_WL).then(res => {
      if (res && res.data && Array.isArray(res.data.reports) && res.data.reports.length > 0) {
        const msNow = (new Date()).getTime();
        const reports = res.data.reports.filter(report => {
          // we only show report up to current date
          if (msNow > new Date(report.from).getTime()) {
            return true;
          } else {
            return false;
          }
        });

        setReports(reports.reverse());
      }
    });
  }, []);

  useEffect(() => {
    (async () => {
      await AppUser.refreshUser(["avatar"]);
      setLoading(false);
    })();
  }, []);

  const getSubmissionStatus = (report) => {
    if (!report.submission) return "Pending";

    const deadline = new Date(report.to);
    const submissionDate = new Date(report.submission.createdDate);

    if (report.submission.status === "Draft") return "In Draft";

    if (submissionDate <= deadline) return "On Time";
    return "Late";
  };

  const getSupervisorFeedback = (report) => {
    if (!report.feedback) return "Pending";
    return "Given";
  };

  const isDeadline = (report, index: number = -1) => {
    if (!report) {
      return false;
    }

    const status = report.submission?.status;
    const msNow = (new Date()).getTime();
    const msSubmissionTo = new Date(report.to).getTime();

    // currently deadline will be red if it's 5 days just before the report's deadline date
    const MS_DEADLINE_DAYS = (1000 * 60 * 60 * 24) * 5;

    // if it's published, no need to mark as deadline
    if (
      status === "Publish" &&
      report.submission?.createdDate
    ) {
      return false;
    }

    // if it's not published, check the submission dedline
    if (
      status !== "Publish" &&
      // only checks for the first item (latest report that needs to be written)
      index === 0 &&
      (msNow >= (msSubmissionTo - MS_DEADLINE_DAYS))
    ) {
      return true;
    }

    return false;
  };

  const getReportUrl = (report) => {
    if (report.submission) {
      if (report.submission.status === "Draft") {
        return ROUTES.PORTAL_WORKPLACE_LEARNING + `/edit/${report.submission._id}`;
      } else {
        return ROUTES.PORTAL_WORKPLACE_LEARNING_DETAIL + `/${report.submission._id}`;
      }
    } else {
      return ROUTES.PORTAL_WORKPLACE_LEARNING_ADD + `?submission=${report?.submissionDateId}`;
    }
  };

  return <div data-comp="CompWLIndex">
    <h1 className="fs-title">Your Particulars</h1>

    <div className="mgr__profile-details mb-3">
      <div>
        <div className="mgr__profile-details__info">
          <p className="label">Name</p>
          <span className="separator">:</span>
          <p className="value value--normal">
            <FallbackEmptyValue text={user?.name} />
          </p>

          <p className="label">Email</p>
          <span className="separator">:</span>
          <p className="value value--normal">
            <FallbackEmptyValue text={user?.email} />
          </p>

          <p className="label">Programme</p>
          <span className="separator">:</span>
          <p className="value value--normal">
            <FallbackEmptyValue text={user?.programmeName} />
          </p>

          <p className="label">Cohort</p>
          <span className="separator">:</span>
          <p className="value value--normal">
            <FallbackEmptyValue text={user?.activeCohort?.name} />
          </p>
        </div>
      </div>
      <div>
        <div className="mgr__profile-details__info">
          <p className="label">IWSP Duration</p>
          <span className="separator">:</span>
          <p className="value value--normal">
            <CohortRangeLabel
              startDate={user?.activeCohort?.startDate}
              endDate={user?.activeCohort?.endDate}
            />
          </p>

          <p className="label">SIT Supervisor</p>
          <span className="separator">:</span>
          <p className="value value--normal">
            <FallbackEmptyValue text={user?.supervisors?.sit} />
          </p>

          <p className="label">Industry Supervisor</p>
          <span className="separator">:</span>
          <p className="value value--normal">
            <FallbackEmptyValue text={user?.supervisors?.industry} />
          </p>
        </div>
      </div>
      <div></div>
    </div>

    <h1 className="fs-title mt-4 mb-3">Your Workplace Learning Reports</h1>

    <div className="wl-reports-table__container">
      {loading && <p className="alert alert-info">Loading. Please wait...</p>}

      {!loading && reports.length > 0 && (
        <div className="wl-reports-table">
          <table className="wl-reports__table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Start Date</th>
                <th>Deadline</th>
                <th>Submission Status</th>
                <th>Supervisor Feedback</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report, index) => (
                <tr
                  key={report._id || index}
                  onClick={() => navigate(getReportUrl(report))}
                  // to={getReportUrl(report)}
                  style={{
                    display: 'table-row',
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer'
                  }}
                  className={`wl-reports__row ${index % 2 === 0 ? 'row-even' : 'row-odd'}`}
                >
                  <td style={{ display: 'table-cell' }}>
                    {reports.length - index}
                  </td>
                  <td style={{ display: 'table-cell' }}>
                    {ModuleDate.getDefaultFormat(report.from)}
                  </td>
                  <td
                    className={isDeadline(report, index) ? 'deadline-urgent' : ''}
                    style={{ display: 'table-cell' }}
                  >
                    {ModuleDate.getDefaultFormat(report.to)}
                  </td>
                  <td style={{ display: 'table-cell' }}>
                    <span className={`status-badge status-${getSubmissionStatus(report).toLowerCase().replace(' ', '-')}`}>
                      {getSubmissionStatus(report)}
                    </span>
                  </td>
                  <td style={{ display: 'table-cell' }}>
                    <span className={`feedback-badge feedback-${getSupervisorFeedback(report).toLowerCase().replace(' ', '-')}`}>
                      {getSupervisorFeedback(report)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && reports.length === 0 && (
        <div className="alert alert-info">
          No Workplace Learning Reports available yet.
        </div>
      )}
    </div>
  </div>;
}
